import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '@/components/shared/ThemeToggle';
import NavigationMenu from './Menu';
import { CommandPalette, Command } from './CommandPalette';
import { getCommandPaletteShortcut } from '@/utils/osDetection';

interface HeaderProps {
    /**
     * User information to display in the header
     */
    userInfo?: {
        username: string;
        role?: string;
        full_name?: string;
        first_name?: string;
    } | null;
    /**
     * Whether to show the navigation menu
     */
    showNavigation?: boolean;
    /**
     * Custom title to display instead of default
     */
    title?: string;
    /**
     * Custom subtitle to display below title
     */
    subtitle?: string;
    /**
     * Custom logo component or element
     */
    logo?: React.ReactNode;
    /**
     * Whether to show theme toggle
     */
    showThemeToggle?: boolean;
    /**
     * Custom navigation options
     */
    customNavigation?: Array<{
        label: string;
        path: string;
        icon: string;
        onClick?: () => void;
    }>;
    /**
     * Custom navigation menu component
     */
    customNavMenu?: React.ReactNode;
    /**
     * Custom user info display
     */
    customUserInfo?: React.ReactNode;
    /**
     * Custom styling overrides
     */
    className?: string;
    /**
     * Background styling
     */
    backgroundClass?: string;
    /**
     * Whether to use dark theme styling
     */
    isDark?: boolean;
    /**
     * Custom welcome message
     */
    welcomeMessage?: string;
    /**
     * Custom navigation menu button
     */
    customNavButton?: React.ReactNode;
    /**
     * Whether to show the default logo and title
     */
    showDefaultBranding?: boolean;
    /**
     * Command palette commands
     */
    commands?: Command[];
}

/**
 * Enhanced Header component for CGM Sports AI Dashboard.
 * Features customizable branding, navigation menu, and styling options.
 * Supports both default and custom implementations for different dashboards.
 */
const Header: React.FC<HeaderProps> = ({
    userInfo,
    showNavigation = true,
    title,
    subtitle,
    logo,
    showThemeToggle = false,
    customNavigation,
    customNavMenu,
    customUserInfo,
    className = '',
    backgroundClass,
    isDark = false,
    welcomeMessage,
    customNavButton,
    showDefaultBranding = true,
    commands
}) => {
    const [showCommandPalette, setShowCommandPalette] = useState<boolean>(false);

    // Command Palette keyboard shortcut (Cmd/Ctrl + K)
    useEffect(() => {
        // Only enable if commands are provided
        if (!commands || commands.length === 0) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Cmd/Ctrl + K: Open Command Palette
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setShowCommandPalette(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [commands]);

    // Determine background class
    const getBackgroundClass = () => {
        if (backgroundClass) return backgroundClass;
        if (isDark) return 'bg-gray-900/90 backdrop-blur-xl border-b border-gray-700/50';
        return 'bg-white/90 backdrop-blur-xl border-b border-gray-200/50';
    };

    // Determine default title
    const getDefaultTitle = () => {
        if (title) return title;
        return 'Wrestling Dashboard';
    };

    // Determine welcome message
    const getWelcomeMessage = () => {
        if (welcomeMessage) return welcomeMessage;
        if (userInfo?.role === 'coach' && userInfo?.first_name) {
            return `Welcome, Coach ${userInfo.first_name}!`;
        }
        if (userInfo?.role === 'coach' && userInfo?.username) {
            return `Welcome, Coach ${userInfo.username}!`;
        }
        if (userInfo?.first_name) {
            return `Welcome, ${userInfo.first_name}!`;
        }
        if (userInfo?.username) {
            return `Welcome, ${userInfo.username}!`;
        }
        return 'Welcome!';
    };

    return (
        <nav className={`${getBackgroundClass()} sticky top-0 z-50 ${className}`} style={{ minHeight: '72px', height: '72px' }}>
            <div className="max-w-7xl mx-auto px-6 py-4 h-full">
                <div className="flex justify-between items-center h-full">
                    <div className="flex items-center space-x-6 h-full flex-1">
                        {/* Navigation Menu */}
                        <NavigationMenu
                            userInfo={userInfo}
                            isDark={isDark}
                            customNavigation={customNavigation}
                            customNavButton={customNavButton}
                            showNavigation={showNavigation}
                            className="my-auto"
                        />

                        {/* Logo and Branding */}
                        {showDefaultBranding && (
                            <div className="flex items-center space-x-4 justify-center sm:justify-start w-full">
                                <div className="hidden sm:block">
                                    {logo ? (
                                        logo
                                    ) : (
                                        <img
                                            className="h-12 w-auto"
                                            src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                                            alt="Team Logo"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = isDark
                                                    ? "/logos/LOGO-MAIN-WHITE.png"
                                                    : "/logos/LOGO-MAIN-BLK.png";
                                            }}
                                        />
                                    )}
                                </div>
                                <div className="flex flex-col min-w-0 items-center sm:items-start text-center sm:text-left">
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent truncate">
                                        {getDefaultTitle()}
                                    </h1>
                                    {subtitle && (
                                        <span className="hidden md:inline text-sm cgm-text-secondary font-medium">
                                            {subtitle}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* User Info and Controls */}
                    <div className="flex items-center space-x-4">
                        {/* Show custom user info if provided, otherwise show welcome message and command palette */}
                        {customUserInfo ? (
                            customUserInfo
                        ) : (
                            <>
                                {/* Welcome Message */}
                                {userInfo && (
                                    <div className={`hidden sm:block bg-gradient-to-r ${isDark ? 'from-red-900/30 to-orange-900/30' : 'from-red-50 to-orange-50'} px-4 py-2 rounded-xl`}>
                                        <span className="text-sm font-medium cgm-text-primary">
                                            {getWelcomeMessage()}
                                        </span>
                                    </div>
                                )}
                                {/* Quick Actions Button */}
                                {commands && commands.length > 0 && (
                                    <button
                                        onClick={() => setShowCommandPalette(true)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 ${isDark
                                            ? 'bg-gray-800/80 hover:bg-gray-700 border border-gray-700'
                                            : 'bg-white/80 hover:bg-gray-50 border border-gray-200'
                                            } shadow-sm hover:shadow-md`}
                                        title="Open Command Palette"
                                    >
                                        <svg className="w-4 h-4 cgm-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <span className="text-sm cgm-text-secondary">Quick Actions</span>
                                        <kbd className={`px-1.5 py-0.5 text-xs rounded font-mono ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {getCommandPaletteShortcut()}
                                        </kbd>
                                    </button>
                                )}
                            </>
                        )}
                        {/* Theme Toggle */}
                        {showThemeToggle && <ThemeToggle />}
                    </div>
                </div>
            </div>

            {/* Command Palette */}
            {commands && commands.length > 0 && (
                <CommandPalette
                    isOpen={showCommandPalette}
                    onClose={() => setShowCommandPalette(false)}
                    commands={commands.filter(cmd =>
                        !cmd.roles ||
                        cmd.roles.length === 0 ||
                        (userInfo?.role && cmd.roles.includes(userInfo.role))
                    )}
                />
            )}
        </nav>
    );
};

export default Header; 